import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormHelperText,
  Grid,
  Link,
  TextField,
  Typography,
  Button as MuiButton,
} from "@mui/material";
import { ReadOnlyTooltip } from "@/components/Tooltip/ReadOnlyTooltip";
import {
  Button,
  ButtonVariantOption,
  SubmitButton,
} from "../../../components/Button";
import { SizingWrapperStyle } from "@/components/Wrapper";
import { PagePaper, SectionPaper } from "@/components/Paper";
import { PageTitle } from "@/components/Title";
import { KeyboardArrowLeft } from "@mui/icons-material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import React, { useState } from "react";
import { useValidator } from "../hooks/useValidator";
import { useAnalyzeAmount } from "@/hooks/useAnalyzeAmount";
import { FileUploadButton } from "@/features/realEstateReceptionBookFeed/components/FileUploadButton";
import { validateAnalysisUploadFiles } from "@/features/analysisUpload/validateAnalysisUploadFiles";
import {
  getAnalysisUploadErrorMessage,
  type AnalysisUploadErrorResponse,
} from "@/features/analysisUpload/getAnalysisUploadErrorMessage";
import {
  preventNonNumericValueWhenPaste,
  preventNonNumericValueWithoutBackspaceWhenKeyDown,
} from "@/utils/utils";
import { useApiClient } from "@/hooks/useApiClient";
import { toast } from "react-toastify";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { type HandOffMessage } from "@/types/handOffMessage";
import { HttpStatusCode, isAxiosError } from "axios";
import { useFeatureFlags } from "@/configs/featureFlag";
import { Label } from "@/components/Label";
import { useReadOnlyUser } from "@/hooks/useReadOnlyUser";
import { READ_ONLY_TOOLTIP_MESSAGE } from "@/utils/permissions";
import {
  AllMattersAnalyzeOptionsCheckbox,
  type AllMattersAnalyzeOptions,
} from "@/components/AcquireBook/AllMattersAnalyzeOptionsCheckbox";

export const AllMattersPDFUpload: React.FC = () => {
  // 識別番号
  const [identifierNumber, setIdentifierNumber] = useState<string>("");
  // 識別名
  const [identifierName, setIdentifierName] = useState<string>("");

  // 確認画面のモーダルを表示するかしないかを決めるフラグ
  const [openModalFlag, setOpenModalFlag] = React.useState(false);

  const [filesToUpload, setFilesToUpload] = useState<File[]>([]);

  // バリデーションのためのフックを導入する
  const validator = useValidator();
  // ファイルのバリデーションエラー状態を追加
  const [fileError, setFileError] = useState<string | null>(null);

  const navigate = useNavigate();
  const { apiClient } = useApiClient();
  const { mypageTop, analyzeFreeText } = useFeatureFlags();
  const isReadOnly = useReadOnlyUser();

  // loading状態を追跡するステート変数を追加
  const [isAnalyzing, setIsAnalyzing] = useState<boolean>(false);

  // 解析オプション
  const [analysisOptions, setAnalysisOptions] =
    useState<AllMattersAnalyzeOptions>({
      includeTitleSection: true,
      includeKoku: true,
      includeOtsuku: true,
      analyzeMethodType: "rule_based",
    });

  // 解析オプション未選択時のエラーメッセージ（送信ブロック時のみ表示）
  const [analysisOptionsError, setAnalysisOptionsError] = useState<
    string | null
  >(null);

  // 金額情報を取得
  const { data: amountInfo } = useAnalyzeAmount("all-matters");

  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const files = Array.from(event.target.files ?? []);
    if (files.length === 0) {
      return;
    }

    const error = validateAnalysisUploadFiles(files);

    setFilesToUpload(error ? [] : files);
    setFileError(error);
    setOpenModalFlag(false);
  };

  const handleFileUploadButtonClick = (
    _event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    cancelLoading: () => void
  ): void => {
    if (isReadOnly) {
      cancelLoading();
      toast.error(READ_ONLY_TOOLTIP_MESSAGE);
      return;
    }

    if (filesToUpload.length > 0) {
      const formData = new FormData();
      filesToUpload.forEach((file) => {
        formData.append("file", file);
      });

      // 識別番号が空の場合は6桁のランダムな数字を生成
      const taskId = identifierNumber.trim() || generateRandom6DigitNumber();
      formData.append("task_id", taskId);

      formData.append("identification_name", identifierName);
      formData.append(
        "include_title_section",
        String(analysisOptions.includeTitleSection)
      );
      formData.append("include_koku", String(analysisOptions.includeKoku));
      formData.append("include_otsuku", String(analysisOptions.includeOtsuku));
      formData.append("analyze_method_type", analysisOptions.analyzeMethodType);
      apiClient
        .post("/all-matters-analyze/upload", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        })
        .then(() => {
          // 成功時の処理
          const handOffMessage: HandOffMessage = {
            command: "ALL_MATTERS_SHOW_ACCEPT_MESSAGE",
          };
          navigate("/allmatters/list", {
            replace: true,
            state: { handOffMessage },
          });
        })
        .catch((e) => {
          cancelLoading();
          if (
            isAxiosError<AnalysisUploadErrorResponse>(e) &&
            e.response?.status === HttpStatusCode.Forbidden
          ) {
            toast.error(READ_ONLY_TOOLTIP_MESSAGE);
            return;
          }

          // レスポンスの結果が502の時はエラーを無視する
          if (
            isAxiosError<AnalysisUploadErrorResponse>(e) &&
            e.response?.status != null &&
            e.response?.status > 300 &&
            e.response?.status !== 502
          ) {
            console.error(e);
            const message =
              e.response.status === HttpStatusCode.BadRequest ||
              e.response.status === HttpStatusCode.PayloadTooLarge
                ? getAnalysisUploadErrorMessage(e.response.data)
                : undefined;
            toast.error(
              message ?? "全部事項解析ファイルのアップロードに失敗しました"
            );
          }
        });
    }
  };

  const generateRandom6DigitNumber = (): string => {
    let result = "";
    for (let i = 0; i < 6; i++) {
      result += Math.floor(Math.random() * 10).toString();
    }
    return result;
  };

  return (
    <PagePaper>
      <form>
        {/* ページタイトル */}
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", mb: 3 }}>
            <PictureAsPdfIcon sx={{ mr: 1 }} fontSize="large" />
            <PageTitle>全部事項データ化</PageTitle>
          </Box>
          <Box>
            <MuiButton
              href="https://trustart.notion.site/11-1bb14754f2df800c9a8ee6d1b6233ffd#20f14754f2df80fcb051c53b45fbbdd9"
              target="_blank"
              startIcon={<AutoStoriesIcon />}
              variant="text"
            >
              マニュアル
            </MuiButton>
          </Box>
        </Box>
        <SectionPaper>
          <Box sx={{ m: 3 }}>
            <Grid container justifyContent={"center"}>
              <Grid item>
                <Grid container direction="column" spacing={4}>
                  <Grid item>
                    <Label required={true}>識別名</Label>
                    <TextField
                      fullWidth
                      error={validator.identifierName.state.error}
                      label="識別名"
                      helperText={
                        validator.identifierName.state.error
                          ? validator.identifierName.state.helperTextWhenError
                          : "この解析依頼にわかりやすい名前を付けてください"
                      }
                      value={identifierName}
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>
                      ) => {
                        setIdentifierName(event.target.value);
                        validator.identifierName.validate(event.target.value);
                      }}
                    />
                  </Grid>
                  <Grid item>
                    <Label required={false}>識別番号</Label>
                    <TextField
                      fullWidth
                      error={validator.identifierNumber.state.error}
                      label="識別番号"
                      helperText={
                        validator.identifierNumber.state.error
                          ? validator.identifierNumber.state.helperTextWhenError
                          : "この解析依頼にわかりやすい番号を付けてください"
                      }
                      required={false}
                      value={identifierNumber}
                      onKeyDown={
                        preventNonNumericValueWithoutBackspaceWhenKeyDown
                      }
                      onPaste={preventNonNumericValueWhenPaste}
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>
                      ) => {
                        setIdentifierNumber(event.target.value);
                        // 任意項目なのでバリデーションは空文字でもOK
                        validator.identifierNumber.validate(event.target.value);
                      }}
                    />
                  </Grid>
                  <Grid item>
                    <Box
                      display={"flex"}
                      justifyContent="start"
                      alignItems="flex-start"
                      my={2}
                      gap={3}
                    >
                      <FileUploadButton
                        variant={ButtonVariantOption.Outlined}
                        onChange={handleFileChange}
                        multiple
                        accept={".pdf,.zip"}
                        label={
                          filesToUpload.length > 0 ? "選び直す" : "アップロード"
                        }
                      />
                      <Box>
                        <Typography variant="body1" sx={{ lineHeight: 2 }}>
                          {filesToUpload.length > 0
                            ? `${filesToUpload.length}ファイル選択中`
                            : "選択されていません"}
                        </Typography>
                        {filesToUpload.length > 0 && (
                          <Box
                            component="ul"
                            sx={{
                              m: 0,
                              pl: 2,
                              maxHeight: 200,
                              overflowY: "auto",
                            }}
                          >
                            {filesToUpload.map((file, index) => (
                              <Typography
                                component="li"
                                key={`${index}-${file.name}`}
                              >
                                {file.name}
                              </Typography>
                            ))}
                          </Box>
                        )}
                      </Box>
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{
                        lineHeight: 2,
                        overflow: "visible",
                        flex: 1,
                      }}
                      color={"text.secondary"}
                    >
                      PDFは100件まで、ZIPは1ファイル選択できます
                      <br />
                      PDFとZIPは同時に選択できません
                      <br />
                      PDFは1ファイル25MB以下、合計250MB以下で選択してください
                    </Typography>
                    {fileError && (
                      <Typography color="error" role="alert">
                        {fileError}
                      </Typography>
                    )}
                  </Grid>
                  <Grid item>
                    <Label required={false}>解析オプション</Label>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                      }}
                    >
                      <AllMattersAnalyzeOptionsCheckbox
                        value={analysisOptions}
                        onChange={(value) => {
                          setAnalysisOptions(value);
                          setAnalysisOptionsError(null);
                        }}
                      />
                      {analysisOptionsError != null && (
                        <FormHelperText error>
                          {analysisOptionsError}
                        </FormHelperText>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ mb: 2 }} />

          <Grid item mb={2}>
            <Box display="flex" justifyContent="center" alignItems="center">
              <Button
                label={"実行する"}
                variant={ButtonVariantOption.Contained}
                size={SizingWrapperStyle.SMALL}
                disabled={filesToUpload.length === 0}
                onClick={() => {
                  const isIdentifierNameValid =
                    validator.identifierName.validate(identifierName);

                  // 解析オプションは最低1つ選択必須
                  const hasAtLeastOneOption =
                    analysisOptions.includeTitleSection ||
                    analysisOptions.includeKoku ||
                    analysisOptions.includeOtsuku;
                  if (!hasAtLeastOneOption) {
                    setAnalysisOptionsError(
                      "解析オプションは最低1つ選択してください"
                    );
                    return;
                  }
                  setAnalysisOptionsError(null);

                  if (isIdentifierNameValid) {
                    setOpenModalFlag(true);
                  }
                }}
              />
            </Box>
          </Grid>
        </SectionPaper>

        {/* 戻る */}
        <Box sx={{ mt: 2 }}>
          <Link
            component={RouterLink}
            to={mypageTop ? "/mypage" : "/feed"}
            sx={{ display: "inline-block" }}
          >
            <Box sx={{ display: "flex" }}>
              <KeyboardArrowLeft />
              <Typography>
                {mypageTop ? "マイページに戻る" : "不動産の権利異動検索に戻る"}
              </Typography>
            </Box>
          </Link>
        </Box>
      </form>

      {/* 確認のモーダル画面 */}
      <Dialog
        open={openModalFlag}
        onClose={() => {
          setOpenModalFlag(false);
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"確認画面"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            全部事項解析を依頼します。
            <br />
            解析件数に対して、1件あたり以下の料金が発生します。
            <br />
            {analyzeFreeText &&
              "※R.E.DATA Plusで取得したデータは無料で解析できます。"}
          </DialogContentText>
          {amountInfo &&
            typeof amountInfo.price === "number" &&
            amountInfo.price.toLocaleString() && (
              <Box component="ul" sx={{ pl: 5 }}>
                <DialogContentText component="li" id="alert-dialog-amount">
                  {`解析金額: ${amountInfo.price.toLocaleString()}円`}
                </DialogContentText>
              </Box>
            )}
        </DialogContent>
        <DialogActions>
          <Button
            variant={ButtonVariantOption.Outlined}
            onClick={() => {
              setOpenModalFlag(false);
            }}
            label={"戻る"}
            disabled={isAnalyzing} // 解析依頼中は戻るボタンを無効化
          />
          <ReadOnlyTooltip isReadOnly={isReadOnly}>
            <SubmitButton
              variant={ButtonVariantOption.Contained}
              onClick={(event, cancelLoading) => {
                setIsAnalyzing(true);
                const wrappedCancelLoading = (): void => {
                  setIsAnalyzing(false);
                  cancelLoading();
                };
                handleFileUploadButtonClick(event, wrappedCancelLoading);
              }}
              label={"解析依頼する"}
              timeout={120000}
              loadingIndicator={
                <Typography variant="button">解析依頼中...</Typography>
              }
              disabled={isReadOnly}
            />
          </ReadOnlyTooltip>
        </DialogActions>
      </Dialog>
    </PagePaper>
  );
};
